import { ChangeEvent, FC, memo } from 'react';
import { ELV_CENTRE_TERMS_DOCUMENTS } from '../../../constants/app';
import { ALTERNATIVE_POSITIONS } from '../../../models/Onboarding/onboarding-values';
import './Inputs.scss';

/**
 * CheckboxInput Props Interface
 * @interface CheckboxInputProps
 */
interface CheckboxInputProps {
  /** Label text color */
  labelColor?: string;
  /** Checkbox label text */
  label: React.ReactNode;
  /** Unique identifier for the checkbox */
  id?: string;
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Callback when checkbox state changes */
  onChange?: ((checked: boolean) => void);
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional HTML input props */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Theme check flag */
  themeCheck?: boolean;
  /** Whether to bold the label text */
  labelBold?: boolean;
  /** Checkbox help text */
  helpText?: React.ReactNode;
  labelLeft?: boolean;
  labelFontSize?: string;
  labelClass?: string;
  labelSpanClass?: string;
}

/**
 * CheckboxInput Component
 * A reusable checkbox component with customizable label color and accessibility features.
 */
const CheckboxInput: FC<CheckboxInputProps> = ({
  labelColor,
  label,
  helpText,
  id,
  checked,
  onChange,
  disabled = false,
  className = '',
  required = false,
  themeCheck = false,
  inputProps = {},
  labelBold = false,
  labelLeft = false,
  labelFontSize = '16',
  labelClass = '',
  labelSpanClass = '',
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  return (
    themeCheck ? (
      <>
        <input
          type="checkbox"
          className="theme-check-input"
          id={id}
          checked={checked}
          onChange={handleChange}
          required={required}
          aria-required={required}
          aria-checked={checked}
          disabled={disabled}
          {...inputProps}
        />
        <label
          htmlFor={id}
          className={`theme-check-label ${labelClass} fs-${labelFontSize} ${labelBold ? 'fw-600' : ''} ${labelLeft ? 'label-left' : ''}`}
        >
          {(() => {
            if (id === "staff_code") {
              return (
                <>
                  I have read and agree to the <a href={ELV_CENTRE_TERMS_DOCUMENTS?.elvStaffInduction} target='_blank' rel='noopener noreferrer' className='text-black'><span className='fw-bold text-black'>Staff Code of Conduct ELV Policy</span></a>
                </>
              );
            } else if (id === "child_safety_code") {
              return (
                <>
                  I have read and agree to the <a href={ELV_CENTRE_TERMS_DOCUMENTS?.childSafetyCode} target='_blank' rel='noopener noreferrer' className='text-black'><span className='fw-bold text-black'>Child Safety Code of Conduct ELV Policy</span></a>
                </>
              );
            } else if (id === "elv_staff_induction") {
              return (
                <>
                  I have read and agree to the <a href={ELV_CENTRE_TERMS_DOCUMENTS?.staffCode} target='_blank' rel='noopener noreferrer' className='text-black'><span className='fw-bold text-black'>ELV Staff Induction</span></a>
                </>
              );
            } else {
              return (
                <>
                  <span className={`${labelSpanClass}`}>{label}</span>
                  { typeof label === 'string' && label === 'Allow access to Timesheet' &&
                    <span className="fs-13 fw-400 text-grey-medium ps-10px">
                      Ability to manage, approve and decline timesheets
                    </span>
                  }
                  { typeof label === 'string' && label === ALTERNATIVE_POSITIONS.CHEF_COOK && <span className='text-grey-medium'> (Cooking Qualification and Food Handler Certificate are required{helpText ? <>. {helpText}</> : ''})</span>}
                </>
              );
            }
          })()}          
        </label>
      </>
    ) : (
      <div className={`checkbox-input ${disabled ? 'disabled' : ''} ${className}`}>
        <label 
          className="checkbox-input__label"
          htmlFor={id}
        >
          <input
            id={id}
            className="checkbox-input__input"
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            aria-required={required}
            aria-checked={checked}
            {...inputProps}
          />
          <div 
            className="checkmark" 
            style={{ background: checked ? labelColor : undefined }}
            aria-hidden="true"
          />
          <span className="checkbox-label-text">
            {label} { typeof label === 'string' && label === ALTERNATIVE_POSITIONS.CHEF_COOK && <span className='text-grey-medium'>(Cooking Qualification and Food Handler Certificate are required {helpText})</span>}
            {required && <span className="required-asterisk">*</span>}
          </span>
        </label>
      </div>
    )
  );
}

export default memo(CheckboxInput);
